import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { LogOut, Eye, AlertTriangle, Radio, AlertOctagon, Activity, Megaphone, ShieldAlert, Zap, CheckCircle, Menu } from 'lucide-react'
import NotificationBell from './NotificationBell'
import WarCrimesTab from './WarCrimesTab'
import WarCrimeReportButton from './WarCrimeReportButton'
import BlindManOverlay from './BlindManOverlay'
import CallBlindManButton from './CallBlindManButton'
import { clearAllUserState } from '../lib/authCleanup'

// ANU-90 — Referee surface for In-Game Admin (IGA) users.
// Mounted by PlayerView when useEffectiveRole(activeEventId).isIGA is true,
// in place of the normal player UI. IGA sees BOTH factions read-only
// (no fog of war) but cannot write event state.
//
// Tabs:
//   • SITREP    — mission-events feed for the event, both factions visible
//   • WAR CRIMES — placeholder until ANU-89 lands; renders shell + count
//   • COMMS     — player_notifications feed filtered to referee-relevant
//                 types (broadcast, frago, bounty, blind_man, blind_man_resume)
//   • BLIND MAN — placeholder until ANU-91 lands; CallBlindManButton will
//                 mount here as the safety-ceasefire trigger
const TABS = [
  { key: 'sitrep',     label: 'SITREP',     icon: Activity },
  { key: 'war_crimes', label: 'WAR CRIMES', icon: ShieldAlert },
  { key: 'comms',      label: 'COMMS',      icon: Radio },
  { key: 'blind_man',  label: 'BLIND MAN',  icon: AlertOctagon },
]

// Recon-style palette, mirroring PlayerView so the surface swap feels
// continuous. Orange is the referee accent — distinct from the green/cyan
// of the player layer and the purple of GM tiers.
const S = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(ellipse at 20% 10%, rgba(80,40,20,.45), transparent 60%), radial-gradient(ellipse at 80% 90%, rgba(60,30,20,.35), transparent 60%), #14181a',
    color: '#e0e8de',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    background: 'rgba(28, 22, 20, .85)',
    borderBottom: '1px solid rgba(255,140,40,.25)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    background: 'rgba(255,140,40,.12)',
    border: '1px solid rgba(255,140,40,.45)',
    color: '#ff9a3d',
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontSize: 12,
  },
  tabBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4,
    padding: '8px',
    background: 'rgba(28, 22, 20, .5)',
    borderBottom: '1px solid rgba(255,140,40,.18)',
  },
  tab: (active) => ({
    minHeight: 44,
    padding: '8px 4px',
    cursor: 'pointer',
    background: active ? 'rgba(255,140,40,.12)' : 'transparent',
    border: active ? '1px solid rgba(255,140,40,.4)' : '1px solid transparent',
    color: active ? '#ff9a3d' : '#8aa090',
    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  }),
  panel: {
    background: 'rgba(28, 38, 38, .55)',
    border: '1px solid rgba(140, 180, 130, .18)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    padding: 14,
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    color: '#8aa090',
    cursor: 'pointer',
    minHeight: 44,
    minWidth: 44,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageBody: { padding: 14, display: 'flex', flexDirection: 'column', gap: 12 },
  rowMuted: { color: '#5a6a60', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" },
  rowText: { color: '#e0e8de', fontSize: 13, fontFamily: "'JetBrains Mono', monospace" },
  pillType: (color) => ({
    display: 'inline-block',
    padding: '2px 7px',
    background: `${color}1a`,
    border: `1px solid ${color}55`,
    color,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  }),
}

const NOTIF_TYPE_COLORS = {
  broadcast: '#ff9a3d',
  frago: '#b380ff',
  bounty: '#ffcc33',
  blind_man: '#ff2a2a',
  blind_man_resume: '#4dd6c2',
  war_crime: '#ff6a3d',
}

const REFEREE_COMMS_TYPES = ['broadcast', 'frago', 'bounty', 'blind_man', 'blind_man_resume']

export default function RefereeView({ eventId }) {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('sitrep')
  const [event, setEvent] = useState(null)
  const [missionEvents, setMissionEvents] = useState([])
  const [comms, setComms] = useState([])
  const [warCrimeCount, setWarCrimeCount] = useState(0)
  const [liftBusy, setLiftBusy] = useState(false)
  const [liftError, setLiftError] = useState('')
  const [navMenuOpen, setNavMenuOpen] = useState(false)

  // Event header (name / status / is_paused). Subscribe to UPDATE so the
  // BLIND MAN tab body flips back to the "call" UI the same tick the GM
  // resumes — without forcing the IGA to reload.
  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    const load = async () => {
      const { data } = await supabase
        .from('events').select('id, name, status, is_paused')
        .eq('id', eventId).limit(1)
      if (!cancelled) setEvent(data?.[0] || null)
    }
    load()
    const channel = supabase
      .channel(`referee-event-${eventId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'events', filter: `id=eq.${eventId}` },
        () => load())
      .subscribe()
    return () => { cancelled = true; try { supabase.removeChannel(channel) } catch { /* ignore */ } }
  }, [eventId])

  // SITREP — mission_events for this event. mission_events doesn't carry
  // event_id, so we join through bounties. Both factions are visible per
  // spec (no fog of war for referees).
  useEffect(() => {
    if (!eventId || activeTab !== 'sitrep') return
    let cancelled = false
    const load = async () => {
      // Pull all bounty IDs for this event, then mission_events for them.
      // Two-step avoids the embedded-resource permission edge cases of a
      // nested select when RLS is tight.
      const { data: bRows } = await supabase
        .from('bounties').select('id').eq('event_id', eventId)
      const bountyIds = (bRows || []).map(b => b.id)
      if (bountyIds.length === 0) {
        if (!cancelled) setMissionEvents([])
        return
      }
      const { data: meRows } = await supabase
        .from('mission_events')
        .select('id, event_type, notes, faction_id, triggered_by, created_at, bounty_id')
        .in('bounty_id', bountyIds)
        .order('created_at', { ascending: false })
        .limit(100)
      if (!cancelled) setMissionEvents(meRows || [])
    }
    load()
    const channel = supabase
      .channel(`referee-sitrep-${eventId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'mission_events' },
          () => load())
      .subscribe()
    return () => { cancelled = true; try { supabase.removeChannel(channel) } catch { /* ignore */ } }
  }, [eventId, activeTab])

  // COMMS — referee-facing notifications, filtered by type.
  useEffect(() => {
    if (!user?.id || !eventId || activeTab !== 'comms') return
    let cancelled = false
    const load = async () => {
      const { data } = await supabase
        .from('player_notifications')
        .select('id, type, title, body, created_at, read')
        .eq('recipient_id', user.id)
        .in('type', REFEREE_COMMS_TYPES)
        .order('created_at', { ascending: false })
        .limit(80)
      if (!cancelled) setComms(data || [])
    }
    load()
    const channel = supabase
      .channel(`referee-comms-${user.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'player_notifications', filter: `recipient_id=eq.${user.id}` },
        () => load())
      .subscribe()
    return () => { cancelled = true; try { supabase.removeChannel(channel) } catch { /* ignore */ } }
  }, [user?.id, eventId, activeTab])

  // War crime badge count — pre-wired so ANU-89 can light it up without a
  // shell change. Table may not exist yet on dev; swallow that error.
  useEffect(() => {
    if (!eventId) return
    let cancelled = false
    ;(async () => {
      try {
        const { count } = await supabase
          .from('war_crimes')
          .select('id', { count: 'exact', head: true })
          .eq('event_id', eventId)
          .eq('status', 'pending')
        if (!cancelled) setWarCrimeCount(count || 0)
      } catch { /* table not present until ANU-89 */ }
    })()
    return () => { cancelled = true }
  }, [eventId, activeTab])

  const liftBlindMan = async () => {
    setLiftBusy(true); setLiftError('')
    const { error } = await supabase.rpc('resume_from_blind_man', { p_event_id: eventId })
    if (error) {
      setLiftError(error.message || 'Failed to lift BLIND MAN. Signal verbally.')
    }
    setLiftBusy(false)
  }

  const handleSignOut = async () => {
    await clearAllUserState()
    await supabase.auth.signOut()
    window.location.href = '/auth'
  }

  const markRead = async (id) => {
    setComms(prev => prev.map(c => c.id === id ? { ...c, read: true } : c))
    await supabase.from('player_notifications').update({ read: true }).eq('id', id)
  }

  return (
    <div style={S.page}>
      {/* ANU-91 — full-screen ceasefire overlay above everything else.
          IGA can also call BLIND MAN; the overlay still triggers for the
          caller (they get the same "stop play" treatment as everyone). */}
      {eventId && <BlindManOverlay eventId={eventId} />}

      {/* Top nav — REFEREE badge + bell + sign out */}
      <div style={S.nav}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={S.badge}><Eye size={12} /> REFEREE</span>
          {event?.name && (
            <span style={{ fontFamily: "'Rajdhani', sans-serif", color: '#e0e8de', fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {event.name}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
          <NotificationBell tone="recon" />
          <button onClick={handleSignOut} title="Sign out" className="hidden sm:inline-flex" style={S.iconBtn}>
            <LogOut size={18} />
          </button>
          <button
            onClick={() => setNavMenuOpen(o => !o)}
            className="sm:hidden"
            style={S.iconBtn}
            aria-label="More"
          >
            <Menu size={18} />
          </button>
          {navMenuOpen && (
            <>
              <div
                onClick={() => setNavMenuOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.2)' }}
              />
              <div style={{
                position: 'fixed', top: 52, right: 12, zIndex: 10001,
                background: '#1c1614', border: '1px solid rgba(255,140,40,.25)',
                padding: 10, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 8,
                boxShadow: '0 8px 20px rgba(0,0,0,.6)',
              }}>
                <button
                  onClick={() => { setNavMenuOpen(false); handleSignOut() }}
                  style={{ ...S.iconBtn, width: 'auto', justifyContent: 'flex-start', gap: 8, padding: '0 10px', fontSize: 12, fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase' }}
                >
                  <LogOut size={16} /> SIGN OUT
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={S.tabBar}>
        {TABS.map(t => {
          const Icon = t.icon
          const active = activeTab === t.key
          const showBadge = t.key === 'war_crimes' && warCrimeCount > 0
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={S.tab(active)}>
              <div style={{ position: 'relative' }}>
                <Icon size={16} />
                {showBadge && (
                  <span style={{
                    position: 'absolute', top: -6, right: -10,
                    background: '#ff2a2a', color: '#fff', fontSize: 9,
                    fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{warCrimeCount}</span>
                )}
              </div>
              {t.label}
            </button>
          )
        })}
      </div>

      {/* Tab body */}
      <div style={S.pageBody}>
        {activeTab === 'sitrep' && (
          <div style={S.panel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Activity size={14} style={{ color: '#ff9a3d' }} />
              <span style={{ fontFamily: "'Rajdhani', sans-serif", color: '#ff9a3d', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                Mission Feed — All Factions
              </span>
            </div>
            {missionEvents.length === 0 ? (
              <p style={S.rowMuted}>No mission events yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {missionEvents.map(m => (
                  <div key={m.id} style={{ padding: '8px 10px', background: 'rgba(0,0,0,.25)', border: '1px solid rgba(140,180,130,.12)', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <span style={S.pillType('#9adb6e')}>{m.event_type}</span>
                      {m.notes && <p style={{ ...S.rowText, marginTop: 4, overflowWrap: 'anywhere' }}>{m.notes}</p>}
                    </div>
                    <span style={S.rowMuted}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'war_crimes' && (
          <>
            <WarCrimesTab eventId={eventId} mode="iga" />
            {/* IGAs can file as well — useful when a referee witnesses an
                incident directly. Status gate inside the button means it
                only renders during live events. */}
            {event?.status === 'live' && (
              <WarCrimeReportButton eventId={eventId} eventStatus={event?.status} />
            )}
          </>
        )}

        {activeTab === 'comms' && (
          <div style={S.panel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Radio size={14} style={{ color: '#ff9a3d' }} />
              <span style={{ fontFamily: "'Rajdhani', sans-serif", color: '#ff9a3d', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                Referee Comms
              </span>
            </div>
            {comms.length === 0 ? (
              <p style={S.rowMuted}>No referee-facing notifications yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {comms.map(c => {
                  const color = NOTIF_TYPE_COLORS[c.type] || '#9adb6e'
                  return (
                    <button key={c.id} onClick={() => !c.read && markRead(c.id)}
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        background: c.read ? 'rgba(0,0,0,.2)' : 'rgba(255,140,40,.06)',
                        border: c.read ? '1px solid rgba(140,180,130,.12)' : '1px solid rgba(255,140,40,.35)',
                        color: '#e0e8de',
                        cursor: c.read ? 'default' : 'pointer',
                        fontFamily: 'inherit',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <span style={S.pillType(color)}>{c.type.replace('_', ' ')}</span>
                        <span style={S.rowMuted}>{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p style={{ ...S.rowText, marginTop: 4 }}>{c.title}</p>
                      {c.body && <p style={{ ...S.rowMuted, marginTop: 2, fontSize: 11 }}>{c.body}</p>}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'blind_man' && (
          <div style={{ maxWidth: 512, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertOctagon size={14} style={{ color: '#ff2a2a' }} />
              <span style={{ fontFamily: "'Rajdhani', sans-serif", color: '#ff2a2a', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                BLIND MAN — Ceasefire
              </span>
            </div>
            {event?.is_paused ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ padding: 12, background: 'rgba(255,42,42,.08)', border: '1px solid rgba(255,42,42,.4)' }}>
                  <p style={{ ...S.rowText, color: '#ff6a6a', fontWeight: 700 }}>BLIND MAN ACTIVE</p>
                  <p style={S.rowMuted}>Game paused. Only lift when the real-world situation is resolved and play is safe to resume.</p>
                </div>
                {liftError && (
                  <p style={{ color: '#ffcc33', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
                    {liftError} — if this keeps failing, signal verbally.
                  </p>
                )}
                <button
                  type="button"
                  onClick={liftBlindMan}
                  disabled={liftBusy}
                  style={{
                    width: '100%',
                    minHeight: 52,
                    padding: '14px 16px',
                    background: liftBusy ? 'rgba(0,200,80,.06)' : 'rgba(0,200,80,.12)',
                    border: '1px solid rgba(0,200,80,.5)',
                    color: '#00c850',
                    fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
                    fontWeight: 800,
                    fontSize: 16,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    cursor: liftBusy ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    opacity: liftBusy ? 0.7 : 1,
                  }}
                >
                  <CheckCircle size={18} />
                  {liftBusy ? 'LIFTING...' : 'LIFT BLIND MAN'}
                </button>
              </div>
            ) : (
              <>
                <p style={S.rowMuted}>For injury, safety hazard, or any real-world stop. Every player on the event sees a full-screen overlay until the GM lifts it. Use sparingly.</p>
                <CallBlindManButton eventId={eventId} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
